// ============================================================
// calculo.js — REGRA DE NEGÓCIO DA FREQUÊNCIA
//
// Esta é a ÚNICA fonte da verdade do cálculo de presença do
// Revelar Talentos. Tudo que precisar de números de frequência
// (relatório Excel, dashboard, painel do técnico, notificações,
// relatórios comparativos) deve importar daqui — nunca reimplementar.
//
// O módulo é puro: não conhece React, Firebase nem Excel.
// Recebe registros + dispensas e devolve os números calculados.
// Isso o torna testável isoladamente (ver calculo.test.js).
// ============================================================

// ── Configuração de regra de negócio ─────────────────────────
// Teto de justificativas (não-competição) como fração dos dias de treino.
// Programa de alto desempenho: justificativas cobrem no máximo este % dos
// treinos; o excedente vira falta. Para DESATIVAR o teto, troque para 1.
// (Viagem/férias e "justificado ao coordenador" são ISENTOS — abonam 100%.)
export const TETO_JUSTIFICATIVA = 0.50;

// ── Isenção de teto para viagem em família / férias ──────────
// Dispensas INDIVIDUAIS de viagem/férias cobrem 100% (não entram no teto),
// pois são períodos legítimos de recesso familiar. Vale a partir do marco
// abaixo (regra "daqui pra frente") — meses anteriores permanecem com o
// teto aplicado a todas as justificativas, preservando relatórios já
// fechados. Detecção pelo subtipo (contém "viagem" ou "ferias").
export const ISENCAO_VIAGEM_A_PARTIR = { ano: 2026, mes: 6 }; // mes 6 = julho (0-11)

function ehSubtipoViagem(subtipo) {
  const s = (subtipo || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  return s.includes("viagem") || s.includes("ferias");
}

// "Justificado ao coordenador": ausência aprovada explicitamente pela
// coordenação. Como é uma validação humana direta, é ISENTA do teto (abona
// 100% dos dias marcados, igual à viagem/férias).
function ehSubtipoCoordenador(subtipo) {
  const s = (subtipo || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  return s.includes("coordenador");
}

// A isenção só vale para meses a partir do marco (ano/mês).
function isencaoAtiva(mes, ano) {
  if (ano > ISENCAO_VIAGEM_A_PARTIR.ano) return true;
  if (ano < ISENCAO_VIAGEM_A_PARTIR.ano) return false;
  return mes >= ISENCAO_VIAGEM_A_PARTIR.mes;
}

// ── Normalização de modalidade ───────────────────────────────
export const MODALIDADES_LABEL = {
  bmx: "BMX",
  skate: "Skate",
  tenis_de_mesa: "Tênis de Mesa",
  "tenis de mesa": "Tênis de Mesa",
  "tênis de mesa": "Tênis de Mesa",
  tenis: "Tênis de Mesa",
  coordenacao: "Coordenação",
  coordenação: "Coordenação",
  "preparacao fisica": "Preparação Física",
  "preparação física": "Preparação Física",
  administrativo: "Administrativo",
};

export function normalizarModalidade(m) {
  if (!m) return "Não informada";
  const lower = m.toLowerCase().trim();
  return MODALIDADES_LABEL[lower] || m;
}

// ── Média de frequência de um grupo ──────────────────────────
// Fonte única para TODA média exibida (resumo, modalidade, comparativo,
// Excel, PDF). Antes cada tela somava por conta própria, e bastava
// esquecer uma para os números divergirem entre relatórios.
//
// Atleta INATIVO fica de fora do cálculo. Quando alguém é excluído no
// meio do mês, os registros dele continuam no banco — e devem continuar,
// porque aconteceram —, mas o percentual dele é de um período parcial e
// puxaria a média da modalidade para baixo. A média responde "como está
// quem faz parte do programa hoje", então conta só quem está ativo.
//
// Devolve null quando não há nenhum ativo: é diferente de 0%, e quem
// exibe decide se mostra "—" ou zero.
export function mediaPctTotal(membros) {
  const ativos = somenteAtivos(membros);
  if (ativos.length === 0) return null;
  return ativos.reduce((soma, m) => soma + m.pctTotal, 0) / ativos.length;
}

// Os que entram na média. Exposto porque as telas precisam mostrar o
// número de atletas ao lado do percentual — se exibissem o total e a
// média fosse só dos ativos, os dois não bateriam e pareceria erro.
export function somenteAtivos(membros) {
  return (membros || []).filter((m) => !m.inativo);
}

// Normalizar nome: remove acentos, lowercase, trim
export function normNome(n) {
  return (n || "").toLowerCase().trim()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

// ── Paleta de cores por modalidade (dinâmica, não fixa a nomes) ──
// Cada modalidade recebe uma cor da paleta, na ordem de aparição.
export const PALETA_MODALIDADE = [
  { cor: "FF7C3AED", bg: "FFEDE9FE", txt: "FF7C3AED" }, // roxo
  { cor: "FF0891B2", bg: "FFE0F2FE", txt: "FF0891B2" }, // ciano
  { cor: "FFD97706", bg: "FFFFF8E1", txt: "FFB45309" }, // âmbar
  { cor: "FF059669", bg: "FFD1FAE5", txt: "FF047857" }, // verde
  { cor: "FFDB2777", bg: "FFFCE7F3", txt: "FFBE185D" }, // rosa
  { cor: "FF2563EB", bg: "FFDBEAFE", txt: "FF1D4ED8" }, // azul
  { cor: "FFCA8A04", bg: "FFFEF9C3", txt: "FFA16207" }, // amarelo
  { cor: "FF64748B", bg: "FFF1F5F9", txt: "FF475569" }, // cinza (fallback)
];

export function corDaModalidade(modalidadesOrdenadas, mod) {
  const i = modalidadesOrdenadas.indexOf(mod);
  return PALETA_MODALIDADE[i >= 0 && i < PALETA_MODALIDADE.length - 1 ? i : PALETA_MODALIDADE.length - 1];
}

// ── Classificação em 5 faixas (sobre o % Total) ──────────────
// Muito bom 90–100 / Bom 80–89 / Regular 70–79 / Ruim 50–69 / Muito ruim 0–49
// (régua definida pelo programa — vinculada às ações de auxílio financeiro)
export function classificar(p) {
  if (p >= 0.90) return { label: "🌟 Muito bom",  cor: "FF00853B", bg: "FFD1FAE5" };
  if (p >= 0.80) return { label: "✔ Bom",         cor: "FF1D6FA4", bg: "FFDBEAFE" };
  if (p >= 0.70) return { label: "⚠ Regular",     cor: "FFD97706", bg: "FFFFF3CD" };
  if (p >= 0.50) return { label: "🔻 Ruim",       cor: "FFEA580C", bg: "FFFFE4CC" };
  return                { label: "⛔ Muito ruim", cor: "FFB91C1C", bg: "FFFEE2E2" };
}

// Um valor de referência dentro de cada faixa de classificar(), na ORDEM
// certa (melhor pra pior). Usado por qualquer tela/relatório que precise
// iterar as 5 faixas e pegar a cor exata de cada uma via classificar(ref) —
// evita duplicar os labels/cores em cada arquivo que monta um gráfico ou
// tabela de distribuição por faixa (Dashboard no painel, Excel por
// modalidade, etc.). Fonte única, junto com classificar() acima.
export const FAIXAS_REFERENCIA = [
  { label: "🌟 Muito bom", ref: 0.95 },
  { label: "✔ Bom", ref: 0.85 },
  { label: "⚠ Regular", ref: 0.75 },
  { label: "🔻 Ruim", ref: 0.60 },
  { label: "⛔ Muito ruim", ref: 0.25 },
];

// ── Classificação de perfil em grupos amplos ─────────────────
// 1 = coordenação/admin, 2 = equipe técnica, 3 = atleta, 6 = outro.
// NÃO fixa modalidade — a separação por modalidade é dinâmica.
export function classificarBloco(u) {
  const p = normNome(u.perfil);
  const m = normNome(u.modalidade);
  if (p.includes("coordenador") || p.includes("supervisor") || p.includes("administrativo") || m === "coordenacao" || m === "administrativo") return 1;
  // "prepara" e não "preparador": o perfil "Auxiliar de Preparação" existe
  // no cadastro e caía em OUTRO, ficando de fora da contagem de atleta e
  // da de equipe ao mesmo tempo, sem aparecer em relatório nenhum.
  if (p.includes("tecnico") || p.includes("monitor") || p.includes("prepara") || p.includes("equipe_tecnica")) return 2;
  if (p.includes("atleta")) return 3;
  return 6;
}

export function grupoTag(u) {
  const b = classificarBloco(u);
  return b === 1 ? "COORD" : b === 2 ? "TECNICO" : b === 3 ? "ATLETA" : "OUTRO";
}

// ── Helpers internos ─────────────────────────────────────────
const toISO = s => (s && s.includes("/")) ? s.split("/").reverse().join("-") : s;

// Busca difusa de nome num índice (lida com nomes curtos digitados no painel)
export function buscarIndiceNome(indice, nomeCompleto) {
  const nm = normNome(nomeCompleto);
  if (indice[nm]) return indice[nm];
  const partesAtleta = nm.split(" ").filter(Boolean);
  const resultado = new Set();
  Object.entries(indice).forEach(([chave, datas]) => {
    const partesChave = chave.split(" ").filter(Boolean);
    if (partesChave.length === 1) {
      if (partesAtleta[0] === partesChave[0] || partesAtleta[partesAtleta.length - 1] === partesChave[0])
        datas.forEach(d => resultado.add(d));
    } else {
      if (partesAtleta[0] === partesChave[0] && partesAtleta.some(p => partesChave.slice(1).includes(p)))
        datas.forEach(d => resultado.add(d));
    }
  });
  return resultado;
}

// Chave de deduplicação: primeiro nome + último sobrenome + modalidade
function chaveUsuario(r) {
  const partes = normNome(r.userName || r.email).split(" ").filter(Boolean);
  const primeiro = partes[0] || "";
  const ultimo = partes[partes.length - 1] || "";
  const mod = normalizarModalidade(r.modalidade).toLowerCase();
  return `${primeiro}_${ultimo}_${mod}`;
}

// ============================================================
// FUNÇÃO PRINCIPAL
//
// Entrada:
//   registros  — logs de presença (attendance_logs) já filtrados por núcleo/mês
//   dispensas  — dispensas do mês (COLETIVA / MODALIDADE / INDIVIDUAL)
//   mes        — 0-11 (JavaScript)
//   ano        — ex: 2026
//
// Saída:
//   { totalTreinos, diasTreino, membros, modalidadesAtletas }
//
//   membros — um objeto por pessoa, JÁ CALCULADO, ordenado por nome:
//     { nome, perfil, modalidade, nucleo, presFisica, competicao,
//       presEfetiva, jc, jm, ji, faltas, pctAbs, pctJust, pctTotal,
//       classif, grupo }
// ============================================================
export function calcularFrequencia({ registros, dispensas, mes, ano, dataCorte = null, nomesAtuais = null, tempoReal = true }) {
  const disp = dispensas || [];
  const regs = registros || [];
  // nomesAtuais (opcional): mapa { emailMinusculo: "Nome Completo" } OU
  // { emailMinusculo: { nome, ativo } }. Quando fornecido, o relatório usa o
  // nome ATUAL do cadastro e sabe se a pessoa está inativa (para marcar
  // "(inativo)"). Aceita as duas formas por retrocompatibilidade.
  const mapaNomes = nomesAtuais || {};
  function dadosCadastro(r) {
    const em = (r.email || "").trim().toLowerCase();
    const v = em ? mapaNomes[em] : null;
    if (v == null) return { nome: r.userName, inativo: false };
    if (typeof v === "string") return { nome: v, inativo: false };
    return { nome: v.nome || r.userName, inativo: v.ativo === false };
  }

  // ── Agrupa os registros por pessoa ──
  const usuariosMapa = {};
  regs.forEach(r => {
    if (!r.userName || r.userName === r.email || r.perfil === "não_informado") return;
    const key = chaveUsuario(r);
    if (!usuariosMapa[key]) {
      const dc = dadosCadastro(r);
      usuariosMapa[key] = {
        nome: dc.nome,
        inativo: dc.inativo,
        perfil: r.perfil,
        modalidade: normalizarModalidade(r.modalidade),
        nucleo: r.nucleo,
        email: r.email,
        diasPresente: new Set(),
      };
    }
    if (r.perfil && r.perfil.length > usuariosMapa[key].perfil.length) {
      usuariosMapa[key].perfil = r.perfil;
    }
    // Se este registro tem email que casa com o cadastro, garante o nome atual
    // e o status ativo (caso o 1º registro do grupo não tivesse email).
    const dcAtual = dadosCadastro(r);
    if (dcAtual.nome && dcAtual.nome !== r.userName) {
      usuariosMapa[key].nome = dcAtual.nome;
    }
    const emReg = (r.email || "").trim().toLowerCase();
    if (emReg && mapaNomes[emReg]) usuariosMapa[key].inativo = dcAtual.inativo;
    const diaKey = r.dateKey || (r.data ? r.data.split("/").reverse().join("-") : null);
    // Só conta registros em DIA DE TREINO (segunda a sexta). Registros em
    // sábado/domingo são ignorados — não há treino no fim de semana, então
    // não podem inflar a presença nem o percentual (mesmo que existam no
    // banco por engano ou manobra). A checagem usa meia-dia para evitar
    // deslocamento de fuso ao interpretar a data ISO.
    if (diaKey) {
      const dow = new Date(diaKey + "T12:00:00").getDay(); // 0=dom, 6=sáb
      if (dow >= 1 && dow <= 5) usuariosMapa[key].diasPresente.add(diaKey);
    }
  });

  // ── Dias úteis do mês (seg–sex) ──
  const primeiroDia = new Date(ano, mes, 1);
  const ultimoDia = new Date(ano, mes + 1, 0);
  const datasColetivas = disp.filter(d => d.tipo === "COLETIVA");
  // toISO também na coletiva (mesma normalização das demais dispensas) —
  // sem isso, coletiva salva como "dd/mm/aaaa" nunca casa com os dias.
  const datasColativasSet = new Set(datasColetivas.map(d => toISO(d.data)));
  const datasModalidadeArr = disp.filter(d => d.tipo === "MODALIDADE");

  // ── Data de corte (cálculo "até o dia", não o mês previsto inteiro) ──
  // O denominador conta só os dias de treino que JÁ aconteceram. Dias futuros
  // não entram — não são falta, apenas ainda não aconteceram.
  //   - Mês corrente: corta em hoje SE o treino de hoje já terminou (18h30),
  //     senão corta ontem. Assim o dia atual não vira falta logo de manhã,
  //     antes de o treino acontecer — só fecha depois do horário do treino.
  //   - Mês já fechado: mês inteiro (o corte cai depois do último dia).
  //   - dataCorte explícita (intervalo por datas): usa ela.
  const HORA_FECHAMENTO = 18 * 60 + 30; // 18h30 (fim dos treinos), em minutos
  const OFFSET_FORTALEZA_H = -3;         // Fortaleza = UTC-3 (sem horário de verão)
  const agora = new Date();
  // Minutos desde a meia-noite no horário de Fortaleza (independe do fuso do
  // dispositivo — o cálculo roda igual no navegador do usuário e no servidor).
  const minutosFortaleza = ((agora.getUTCHours() + OFFSET_FORTALEZA_H + 24) % 24) * 60 + agora.getUTCMinutes();
  const treinoDeHojeJaFechou = minutosFortaleza >= HORA_FECHAMENTO;
  const hoje = new Date();
  if (treinoDeHojeJaFechou) {
    hoje.setHours(23, 59, 59, 999);  // treino de hoje já terminou: inclui hoje
  } else {
    hoje.setHours(0, 0, 0, 0);
    hoje.setMilliseconds(-1);        // ainda em treino/antes: corta ontem
  }
  const corte = dataCorte ? new Date(dataCorte + "T23:59:59") : (ultimoDia < hoje ? ultimoDia : hoje);

  const diasTreino = [];
  for (let d = new Date(primeiroDia); d <= ultimoDia; d.setDate(d.getDate() + 1)) {
    const dow = d.getDay();
    if (dow >= 1 && dow <= 5 && d <= corte) diasTreino.push(d.toISOString().split("T")[0]);
  }
  const totalTreinos = diasTreino.length;

  // ── DIA DE HOJE "AGUARDANDO" (Opção A) ──
  // O dia atual, enquanto o treino ainda não fechou (antes das 18h30), APARECE
  // na grade como um dia à espera de registros — cinza para quem ainda não
  // registrou, verde para quem já registrou — MAS não entra em diasTreino, ou
  // seja, NÃO conta no denominador nem no percentual. Só quando o treino fecha
  // (18h30) o dia passa a contar normalmente (aí já está em diasTreino) e quem
  // não registrou vira falta. Isso não altera nenhum número do cálculo; apenas
  // adiciona uma coluna visual "aguardando" ao mapaDiario.
  // Só vale para o mês/ano corrente, em dia útil, fora de coletiva, e quando
  // não há dataCorte explícita (intervalo por datas não tem "hoje aguardando").
  let diaHojeAguardando = null;
  if (!dataCorte && !treinoDeHojeJaFechou) {
    const ag = new Date();
    const isoHoje = new Date(ag.getTime() - ag.getTimezoneOffset() * 60000).toISOString().split("T")[0];
    const dowHoje = new Date(isoHoje + "T12:00:00").getDay();
    const dentroDoMes = ag.getMonth() === mes && ag.getFullYear() === ano;
    if (dentroDoMes && dowHoje >= 1 && dowHoje <= 5 && !datasColativasSet.has(isoHoje) && !diasTreino.includes(isoHoje)) {
      diaHojeAguardando = isoHoje;
    }
  }
  // ── PRESENÇA DE HOJE CONTA NA HORA; FALTA DE HOJE ESPERA FECHAR ──
  // Antes das 18h30 o dia de hoje ficava fora da conta de TODO MUNDO, para
  // que ninguém levasse falta por um treino ainda acontecendo. O efeito
  // colateral era que a presença de quem já tinha registrado também não
  // contava: o quadrado ficava verde na grade e o percentual não se movia
  // até o fim da tarde.
  //
  // Com tempoReal, o dia de hoje entra no numerador E no denominador do
  // atleta que registrou, e continua fora para quem não registrou. Ou
  // seja, cada atleta tem o seu próprio corte, que é o certo: para quem
  // treinou, o dia aconteceu; para quem ainda não, ele não terminou.
  //
  // Isso nunca derruba o percentual de ninguém, porque acrescenta um dia
  // de presença aos DOIS lados da fração, o que só aproxima de 100%.
  //
  // Fica desligado onde o número vira documento (relatório e planilha):
  // ali dois atletas da mesma modalidade apareceriam com denominadores
  // diferentes no mesmo mês, e isso é indefensável num PDF oficial.
  function hojeContaPara(u) {
    if (!tempoReal || !diaHojeAguardando) return false;
    const mod = normalizarModalidade(u.modalidade || "").toLowerCase();
    if ((dayoffMod[mod] || new Set()).has(diaHojeAguardando)) return false;
    return [...u.diasPresente].some(d => toISO(d) === diaHojeAguardando);
  }

  // Dias exibidos na grade = dias que contam + (se houver) o dia de hoje aguardando.
  const diasVisiveis = diaHojeAguardando ? [...diasTreino, diaHojeAguardando] : diasTreino;

  const ehDiaTreino = iso => diasTreino.includes(iso) && !datasColativasSet.has(iso);

  // Subtipos por data — para o mapa diário mostrar o MOTIVO de cada dia.
  // Aditivo: não afeta nenhum cálculo, só rotula os dias.
  const subCol = {};   // iso -> subtipo da coletiva
  datasColetivas.forEach(d => { subCol[toISO(d.data)] = d.subtipo || "Coletiva"; });
  const subMod = {};   // "modKey|iso" -> subtipo da dispensa de modalidade
  datasModalidadeArr.forEach(d => {
    const mod = normalizarModalidade(d.modalidade || "").toLowerCase();
    subMod[`${mod}|${toISO(d.data)}`] = d.subtipo || "Modalidade";
  });
  const subIndiv = {}; // "nomeNorm|iso" -> subtipo da individual
  disp.filter(d => d.tipo === "INDIVIDUAL").forEach(d => {
    subIndiv[`${normNome(d.nome || "")}|${toISO(d.data)}`] = d.subtipo || "Individual";
  });

  // ── Índices por modalidade e individual ──
  // Separa COMPETIÇÃO (conta como presença) do restante (justificativa).
  // Descobre todas as modalidades presentes (dos atletas) para poder expandir
  // dispensas de modalidade marcadas como "Todas".
  const modalidadesPresentes = Array.from(new Set(
    Object.values(usuariosMapa)
      .filter(u => grupoTag(u) === "ATLETA")
      .map(u => normalizarModalidade(u.modalidade || "").toLowerCase())
      .filter(m => m && m !== "não informada")
  ));

  const dispMod = {}, compMod = {}, dayoffMod = {};
  datasModalidadeArr.forEach(d => {
    // Chave normalizada (mesma forma canônica do resto do sistema) — sem
    // isso, "Tênis de Mesa" (com acento) e "Tenis de Mesa" (sem) viram
    // chaves diferentes e o abono da modalidade se perde.
    const modRaw = normalizarModalidade(d.modalidade || "").toLowerCase();
    if (!modRaw || modRaw === "não informada") return;
    const sub = (d.subtipo || "").toLowerCase();
    const ehComp = sub.includes("competi");
    const ehDayoff = sub.includes("day off") || sub.includes("day-off") || sub.includes("dayoff");
    // "Todas": a dispensa vale para TODAS as modalidades presentes — expande
    // para cada uma, em vez de ser descartada (o que antes virava falta).
    const modsAlvo = (modRaw === "todas") ? modalidadesPresentes : [modRaw];
    const iso = toISO(d.data);
    modsAlvo.forEach(mod => {
      if (ehDayoff) {
        if (!dayoffMod[mod]) dayoffMod[mod] = new Set();
        dayoffMod[mod].add(iso);
      } else if (ehComp) {
        if (!compMod[mod]) compMod[mod] = new Set();
        compMod[mod].add(iso);
      } else {
        if (!dispMod[mod]) dispMod[mod] = new Set();
        dispMod[mod].add(iso);
      }
    });
  });

  const dispensasIndiv = disp.filter(d => d.tipo === "INDIVIDUAL");
  const dispIndiv = {}, compIndiv = {}, viagemIndiv = {}, coordIndiv = {};
  const viagemLiberada = isencaoAtiva(mes, ano);
  dispensasIndiv.forEach(d => {
    const nm = normNome(d.nome || "");
    if (!nm) return;
    const ehComp = (d.subtipo || "").toLowerCase().includes("competi");
    // Viagem/férias individual (a partir do marco) vai para um balde
    // próprio, ISENTO do teto — cobre 100% dos dias marcados.
    if (viagemLiberada && !ehComp && ehSubtipoViagem(d.subtipo)) {
      if (!viagemIndiv[nm]) viagemIndiv[nm] = new Set();
      viagemIndiv[nm].add(toISO(d.data));
      return;
    }
    // "Justificado ao coordenador": balde próprio, ISENTO do teto (abona
    // 100%). É uma ausência aprovada diretamente pela coordenação.
    if (!ehComp && ehSubtipoCoordenador(d.subtipo)) {
      if (!coordIndiv[nm]) coordIndiv[nm] = new Set();
      coordIndiv[nm].add(toISO(d.data));
      return;
    }
    const alvo = ehComp ? compIndiv : dispIndiv;
    if (!alvo[nm]) alvo[nm] = new Set();
    alvo[nm].add(toISO(d.data));
  });

  // ── Dias de competição do atleta (contam como presença) ──
  // Um dia de DAY OFF (ou coletiva) nunca conta como competição — esses dias
  // saíram do denominador, então não podem entrar como presença (senão o
  // numerador teria um dia que não está no denominador, inflando o %).
  function ehDiaValido(u, iso) {
    const mod = normalizarModalidade(u.modalidade || "").toLowerCase();
    const doDayoff = dayoffMod[mod] || new Set();
    return ehDiaTreino(iso) && !doDayoff.has(iso);
  }
  function diasCompeticao(u) {
    const dias = new Set();
    const mod = normalizarModalidade(u.modalidade || "").toLowerCase();
    (compMod[mod] || new Set()).forEach(iso => { if (ehDiaValido(u, iso)) dias.add(iso); });
    buscarIndiceNome(compIndiv, u.nome).forEach(iso => { if (ehDiaValido(u, iso)) dias.add(iso); });
    // não contar duas vezes dias em que já houve presença física
    [...u.diasPresente].forEach(d => dias.delete(toISO(d)));
    return dias;
  }

  // ── Justificativas não-competição (abono) ──
  function calcJust(u) {
    const jc = [...datasColativasSet].filter(d => {
      const dt = new Date(d + "T12:00:00");
      const dow = dt.getDay();
      return dow >= 1 && dow <= 5;
    }).length;
    const mod = normalizarModalidade(u.modalidade || "").toLowerCase();
    // IMPORTANTE: uma justificativa NÃO conta se o atleta já tem presença
    // física naquele dia — senão o mesmo dia entraria como presença E como
    // abono, inflando o percentual (crédito duplo). A presença tem prioridade.
    // O conjunto de dias presentes já está em ISO (mesmo formato de toISO).
    const presISO = new Set([...u.diasPresente].map(d => toISO(d)));
    const jm = [...(dispMod[mod] || new Set())]
      .filter(iso => ehDiaTreino(iso) && !presISO.has(iso)).length;
    const ji = [...buscarIndiceNome(dispIndiv, u.nome)]
      .filter(iso => ehDiaTreino(iso) && !presISO.has(iso)).length;
    // Dias de viagem/férias individual (isentos do teto). Não contar em
    // duplicidade com dias de presença física.
    const viagem = [...buscarIndiceNome(viagemIndiv, u.nome)]
      .filter(iso => ehDiaTreino(iso) && !presISO.has(iso)).length;
    // Dias justificados ao coordenador (isentos do teto — abonam 100%).
    const coord = [...buscarIndiceNome(coordIndiv, u.nome)]
      .filter(iso => ehDiaTreino(iso) && !presISO.has(iso)).length;
    return { jc, jm, ji, viagem, coord };
  }

  // ── Cálculo consolidado por pessoa ──
  // Denominador por atleta: dias de treino MENOS os dias que não houve
  // treino para ele. Dias sem treino = coletivas (todo o núcleo) + day off da
  // modalidade do atleta. Esses dias saem da conta (não são presença nem
  // falta) — assim um dia sem treino não infla nem penaliza o %.
  function diasSemTreinoDoAtleta(u) {
    const mod = normalizarModalidade(u.modalidade || "").toLowerCase();
    const doDayoff = dayoffMod[mod] || new Set();
    const set = new Set();
    diasTreino.forEach(iso => {
      if (datasColativasSet.has(iso) || doDayoff.has(iso)) set.add(iso);
    });
    return set;
  }
  function treinosDoAtleta(u) {
    return totalTreinos - diasSemTreinoDoAtleta(u).size + (hojeContaPara(u) ? 1 : 0);
  }

  // presença efetiva = física + competição; teto aplicado nas justificativas
  function calcAtleta(u) {
    // Denominador do ATLETA (desconta coletivas + day off da modalidade dele).
    const treinos = treinosDoAtleta(u);
    const semTreino = diasSemTreinoDoAtleta(u); // coletivas + day off da modalidade
    // Presença física: dias registrados que SÃO dia de treino válido (não
    // conta um registro que caiu num feriado/day off — esse dia saiu do
    // denominador, então não pode entrar no numerador e inflar o %).
    const contaHoje = hojeContaPara(u);
    const presFisica = [...u.diasPresente].filter(d => {
      const iso = toISO(d);
      // Hoje entra só para quem registrou, e nesse caso já foi somado ao
      // denominador acima — os dois lados da fração andam juntos.
      if (iso === diaHojeAguardando) return contaHoje;
      return diasTreino.includes(iso) && !semTreino.has(iso);
    }).length;
    const competicao = diasCompeticao(u).size;
    const presEfetiva = presFisica + competicao;
    const { jc, jm, ji, viagem, coord } = calcJust(u);
    const jraw = jm + ji;  // jc (coletivas) não entra: viraram dias sem treino
    const tetoDias = Math.round(treinos * TETO_JUSTIFICATIVA);
    const jeff = Math.min(jraw, tetoDias);
    // Abonos ISENTOS do teto (somam integralmente, além do jeff limitado):
    // viagem/férias e "justificado ao coordenador".
    const abono = jeff + viagem + coord;
    const faltas = Math.max(0, treinos - presEfetiva - abono);
    const pctAbs = treinos > 0 ? presEfetiva / treinos : 0;
    const pctJust = treinos > 0 ? abono / treinos : 0;
    const pctTotal = Math.min(pctAbs + pctJust, 1);
    // A classificação usa o percentual ARREDONDADO pro inteiro — o mesmo
    // número que todas as telas e relatórios exibem. Sem isso, um atleta
    // com 69,57% aparecia como "70%" na tela mas caía na faixa de baixo
    // (Atenção em vez de Regular), porque 0,6957 < 0,70. Arredondar antes
    // também elimina erros de ponto flutuante (0,6999999... ≠ 0,70).
    const pctParaClassificar = Math.round(pctTotal * 100) / 100;

    // ── Mapa diário (aditivo — não altera nenhum número acima) ──
    // Para cada dia de treino do período, classifica o status do atleta.
    // Usa as MESMAS estruturas do cálculo, então é coerente com os totais.
    // Prioridade: presença física > competição > coletiva > justif.
    // modalidade > viagem/férias > justif. individual > falta.
    const modKey = normalizarModalidade(u.modalidade || "").toLowerCase();
    const setViagem = buscarIndiceNome(viagemIndiv, u.nome);
    const setIndiv = buscarIndiceNome(dispIndiv, u.nome);
    const setCoord = buscarIndiceNome(coordIndiv, u.nome);
    const setCompMod = compMod[modKey] || new Set();
    const setCompIndiv = buscarIndiceNome(compIndiv, u.nome);
    const setDispMod = dispMod[modKey] || new Set();
    const setDayoff = dayoffMod[modKey] || new Set();
    const presISO = new Set([...u.diasPresente].map(d => toISO(d)));
    const nmNorm = normNome(u.nome || "");
    const mapaDiario = diasVisiveis.map(iso => {
      let status, subtipo = null;
      // Dia de hoje "aguardando" (não conta no %): se o atleta já registrou,
      // mostra presença (verde na hora); senão, fica cinza "aguardando" — NÃO
      // é falta, pois o treino ainda não fechou.
      if (iso === diaHojeAguardando) {
        status = presISO.has(iso) ? "presenca" : "aguardando";
        return { dia: iso, status, subtipo: status === "aguardando" ? "Aguardando registro" : null };
      }
      if (datasColativasSet.has(iso)) { status = "sem_treino"; subtipo = subCol[iso] || "Sem treino"; }
      else if (setDayoff.has(iso)) { status = "sem_treino"; subtipo = subMod[`${modKey}|${iso}`] || "Day off"; }
      else if (presISO.has(iso)) status = "presenca";
      else if (setCompMod.has(iso) || setCompIndiv.has(iso)) { status = "competicao"; subtipo = subMod[`${modKey}|${iso}`] || subIndiv[`${nmNorm}|${iso}`] || "Competição"; }
      else if (setDispMod.has(iso)) { status = "justif_modalidade"; subtipo = subMod[`${modKey}|${iso}`] || "Dispensa de modalidade"; }
      else if (setViagem.has(iso)) { status = "viagem"; subtipo = subIndiv[`${nmNorm}|${iso}`] || "Viagem/férias"; }
      else if (setCoord.has(iso)) { status = "justif_individual"; subtipo = subIndiv[`${nmNorm}|${iso}`] || "Justificado ao coordenador"; }
      else if (setIndiv.has(iso)) { status = "justif_individual"; subtipo = subIndiv[`${nmNorm}|${iso}`] || "Justificada"; }
      else status = "falta";
      return { dia: iso, status, subtipo };
    });

    return {
      nome: u.nome, perfil: u.perfil, modalidade: u.modalidade, nucleo: u.nucleo, inativo: u.inativo || false,
      presFisica, competicao, presEfetiva, jc, jm, ji: ji + viagem + coord, jiComum: ji, viagem, coord, faltas,
      treinos, // denominador do atleta (desconta coletivas + day off da modalidade)
      pctAbs, pctJust, pctTotal, classif: classificar(pctParaClassificar), grupo: grupoTag(u),
      mapaDiario,
    };
  }

  // Calcula UMA VEZ para todos, ordenado por nome.
  const membros = Object.values(usuariosMapa)
    .sort((a, b) => a.nome.localeCompare(b.nome))
    .map(u => calcAtleta(u));

  // Modalidades presentes entre os ATLETAS (derivadas dos dados).
  const modalidadesAtletas = Array.from(new Set(
    membros.filter(m => m.grupo === "ATLETA").map(m => m.modalidade).filter(Boolean)
  )).sort();

  return { totalTreinos, diasTreino, diasVisiveis, diaHojeAguardando, membros, modalidadesAtletas };
}